import { useState } from 'react';
import clsx from 'clsx';
import { ChevronDown, ChevronRight, TrendingUp, TrendingDown } from 'lucide-react';
import { formatOdds, formatMarket } from '../../utils/format';
import { AnimatePresence } from 'framer-motion';
import { FullAnalysisModal } from '../BetCard/FullAnalysisModal';
import type { BetMarket, BetRecommendation } from '../../types';
import type { MLBGame } from '../../types';

interface MarketRowProps {
  market: BetMarket;
  label: string;
  /** Away team / Over side */
  awayLabel: string;
  awayOdds?: number;
  /** Home team / Under side */
  homeLabel: string;
  homeOdds?: number;
  /** Spread label for run line (e.g. "+1.5") shown next to away odds */
  awaySpreadLabel?: string;
  /** Spread label for run line (e.g. "-1.5") shown next to home odds */
  homeSpreadLabel?: string;
  game: MLBGame;
  gameDate: string;
  /** AI recommendation for this specific market */
  recommendation?: BetRecommendation;
  /** When true (game has a QUALIFIED/STRONG_BET rec), clicking either side opens the place-bet modal */
  isQualifiedGame?: boolean;
  /** When true, the qualified side is already bet — show green but disabled with tooltip */
  isBetPlaced?: boolean;
  /** Previous odds for flash animation (away/over) */
  prevAwayOdds?: number;
  /** Previous odds for flash animation (home/under) */
  prevHomeOdds?: number;
}

export function MarketRow({
  market,
  label,
  awayLabel,
  awayOdds,
  homeLabel,
  homeOdds,
  awaySpreadLabel,
  homeSpreadLabel,
  game,
  gameDate,
  recommendation,
  isQualifiedGame = false,
  isBetPlaced = false,
}: MarketRowProps) {
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const recIsAway =
    recommendation &&
    (recommendation.selectionType === 'away' || recommendation.selectionType === 'over');
  const recIsHome =
    recommendation &&
    (recommendation.selectionType === 'home' || recommendation.selectionType === 'under');

  const isQualifiedRec =
    recommendation?.decisionStatus === 'STRONG_BET' ||
    recommendation?.decisionStatus === 'QUALIFIED_BET';

  const highlightAway = recIsAway && isQualifiedRec;
  const highlightHome = recIsHome && isQualifiedRec;

  const edgePct = recommendation ? (recommendation.edge * 100).toFixed(1) : null;
  const edgeCls =
    recommendation && recommendation.edge >= 0.07
      ? 'text-win'
      : recommendation && recommendation.edge >= 0.04
        ? 'text-warn'
        : 'text-gray-500';

  const isBetNow = recommendation?.timingRecommendation === 'BET_NOW';
  // STRONG_BET always treated as urgent
  const isUrgent = isBetNow || recommendation?.decisionStatus === 'STRONG_BET';

  const pulseClass = isUrgent ? 'animate-pulse-fast' : 'animate-pulse-slow';

  function handleSideClick(side: 'away' | 'home', e: React.MouseEvent) {
    e.stopPropagation();
    if (isBetPlaced) return;
    setShowModal(true);
  }

  const awaySelection =
    awaySpreadLabel ? `${game.awayTeam.name} ${awaySpreadLabel}` : game.awayTeam.name;
  const homeSelection =
    homeSpreadLabel ? `${game.homeTeam.name} ${homeSpreadLabel}` : game.homeTeam.name;

  // Derive selection type: spread/ML = away|home, total/team totals = over|under
  const awaySelType: 'away' | 'over' =
    market === 'total' ||
    market === 'first5_total' ||
    market === 'team_total_over' ||
    market === 'team_total_under'
      ? 'over'
      : 'away';
  const homeSelType: 'home' | 'under' =
    market === 'total' ||
    market === 'first5_total' ||
    market === 'team_total_over' ||
    market === 'team_total_under'
      ? 'under'
      : 'home';

  return (
    <div className="group">
      {/* Main row */}
      <div className="flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-surface-3/50 transition-colors">
        {/* Market label */}
        <div className="w-24 flex-shrink-0">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            {label}
          </span>
        </div>

        {/* Away odds button */}
        <button
          onClick={(e) => highlightAway && !isBetPlaced ? handleSideClick('away', e) : e.stopPropagation()}
          disabled={!highlightAway || (isBetPlaced && highlightAway)}
          title={isBetPlaced && highlightAway ? 'Bet already placed' : !highlightAway ? undefined : undefined}
          className={clsx(
            'flex-1 flex items-center justify-between gap-1.5 px-3 py-1.5 rounded-md border text-xs font-mono font-semibold transition-all',
            awayOdds == null
              ? 'border-surface-4 text-gray-600 cursor-not-allowed'
              : isBetPlaced && highlightAway
                ? clsx('border-win/60 bg-win/10 text-win/50 ring-1 ring-win/40 cursor-not-allowed opacity-60', pulseClass)
                : highlightAway
                  ? clsx('border-win/60 bg-win/10 text-win ring-1 ring-win/40 hover:border-win/80 hover:bg-win/15 cursor-pointer', pulseClass)
                  : 'border-surface-4 text-gray-500 cursor-not-allowed opacity-50',
          )}
        >
          <span className="text-[10px] text-gray-500 font-sans font-normal truncate">
            {awayLabel}
            {awaySpreadLabel && (
              <span className="ml-1 text-gray-400">{awaySpreadLabel}</span>
            )}
          </span>
          <span>{awayOdds != null ? formatOdds(awayOdds) : '—'}</span>
          {highlightAway && edgePct && (
            <span className={clsx('text-[9px] font-sans', edgeCls)}>+{edgePct}%</span>
          )}
        </button>

        {/* Home odds button */}
        <button
          onClick={(e) => highlightHome && !isBetPlaced ? handleSideClick('home', e) : e.stopPropagation()}
          disabled={!highlightHome || (isBetPlaced && highlightHome)}
          title={isBetPlaced && highlightHome ? 'Bet already placed' : undefined}
          className={clsx(
            'flex-1 flex items-center justify-between gap-1.5 px-3 py-1.5 rounded-md border text-xs font-mono font-semibold transition-all',
            homeOdds == null
              ? 'border-surface-4 text-gray-600 cursor-not-allowed'
              : isBetPlaced && highlightHome
                ? clsx('border-win/60 bg-win/10 text-win/50 ring-1 ring-win/40 cursor-not-allowed opacity-60', pulseClass)
                : highlightHome
                  ? clsx('border-win/60 bg-win/10 text-win ring-1 ring-win/40 hover:border-win/80 hover:bg-win/15 cursor-pointer', pulseClass)
                  : 'border-surface-4 text-gray-500 cursor-not-allowed opacity-50',
          )}
        >
          <span className="text-[10px] text-gray-500 font-sans font-normal truncate">
            {homeLabel}
            {homeSpreadLabel && (
              <span className="ml-1 text-gray-400">{homeSpreadLabel}</span>
            )}
          </span>
          <span>{homeOdds != null ? formatOdds(homeOdds) : '—'}</span>
          {highlightHome && edgePct && (
            <span className={clsx('text-[9px] font-sans', edgeCls)}>+{edgePct}%</span>
          )}
        </button>

        {/* Analytics toggle + edge indicator */}
        <div className="flex items-center gap-1.5 flex-shrink-0 w-24 justify-end">
          {recommendation && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowAnalytics(!showAnalytics);
              }}
              className={clsx(
                'flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded transition-colors',
                showAnalytics ? 'text-brand' : edgeCls,
              )}
              title="Show AI analytics"
            >
              {showAnalytics ? (
                <ChevronDown className="w-3 h-3" />
              ) : (
                <ChevronRight className="w-3 h-3" />
              )}
              {edgePct && `+${edgePct}%`}
            </button>
          )}
          {!recommendation && (
            <span className="text-[10px] text-gray-700">—</span>
          )}
        </div>
      </div>

      {/* Inline analytics expansion */}
      {showAnalytics && recommendation && (
        <div className="mx-3 mb-2 bg-surface-0 border border-surface-4 rounded-lg p-2.5 animate-fade-in">
          <div className="grid grid-cols-4 gap-2 mb-2">
            <MetricMini label="Edge" value={`+${(recommendation.edge * 100).toFixed(1)}%`} cls={edgeCls} />
            <MetricMini
              label="Conf"
              value={(recommendation.confidence * 10).toFixed(1)}
              cls={
                recommendation.confidence >= 0.7
                  ? 'text-win'
                  : recommendation.confidence >= 0.4
                    ? 'text-warn'
                    : 'text-gray-500'
              }
            />
            <MetricMini label="Data" value={`${Math.round(recommendation.dataReliabilityScore * 100)}%`} />
            <MetricMini
              label="Consensus"
              value={`${Math.round(recommendation.consensusScore * 100)}%`}
            />
          </div>
          {recommendation.reasonsFor.length > 0 && (
            <div className="space-y-0.5">
              {recommendation.reasonsFor.slice(0, 2).map((r, i) => (
                <div key={i} className="flex items-start gap-1.5 text-[10px] text-gray-400">
                  <TrendingUp className="w-3 h-3 text-win flex-shrink-0 mt-px" />
                  <span>{r.text}</span>
                </div>
              ))}
              {recommendation.reasonsAgainst.slice(0, 1).map((r, i) => (
                <div key={i} className="flex items-start gap-1.5 text-[10px] text-gray-500">
                  <TrendingDown className="w-3 h-3 text-loss flex-shrink-0 mt-px" />
                  <span>{r.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <AnimatePresence>
        {showModal && recommendation && (
          <FullAnalysisModal
            recommendation={recommendation}
            onClose={() => setShowModal(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function MetricMini({
  label,
  value,
  cls = 'text-gray-300',
}: {
  label: string;
  value: string;
  cls?: string;
}) {
  return (
    <div className="flex flex-col items-center bg-surface-2 rounded px-2 py-1">
      <span className="text-[9px] text-gray-600 uppercase tracking-wider">{label}</span>
      <span className={clsx('text-[11px] font-bold font-mono', cls)}>{value}</span>
    </div>
  );
}
